import styled, { css } from 'vue-styled-components';
import { darken } from 'polished';
import COLORS from '@design/colors.js';

import memoize from 'lodash/memoize';
import { BreakPoint, pickBreakpoint } from '@/utils/layout';

export const ABSOLUTE_BOTH_CENTER = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

export const ABSOLUTE_VERTICAL_CENTER = css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
`;

export const FLEX_CENTER = css`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const FLEX_START = css`
    display: flex;
    justify-content: flex-start;
    align-content: center;
`;

export const CUSTOM_SCROLL = css`
    ::-webkit-scrollbar {
        -webkit-appearance: none !important;
        width: 4px !important;
    }
    ::-webkit-scrollbar-thumb {
        background-color: ${COLORS['gray-25']} !important;
    }
    &::-webkit-scrollbar {
        -webkit-appearance: none !important;
        width: 4px !important;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${COLORS['gray-25']} !important;
    }
`;

export const FLEX_GROW = (value) => {
    return css`
        -webkit-box-flex: ${value};
        -webkit-flex-grow: ${value};
        -ms-flex-positive: ${value};
        flex-grow: ${value};
    `;
};

export const BEFORE_AFTER_CONTENT = css`
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
`;

export const COMMON_BOX_SHADOW = css`
    box-shadow: 0 2px 30px 0 rgba(0, 0, 0, 0.1);
`;

export const FONT_FAMILY = css`
    font-family: 'Noto Sans', 'Noto Sans KR', sans-serif;
`;

export const InlineBlock = styled.span`
    display: inline-block;
`;

export const MainColor = css`
    background-color: #0054c6;
    transition: background 0.2s;

    &:hover {
        background: #519acc;
    }
`;

export const GrayColor = css`
    background-color: #f2f3f5;
    transition: background 0.2s;
    &:hover {
        background: #eee;
    }
`;

export const ConfirmButton = styled.button`
    ${FLEX_CENTER}
    ${MainColor}
    color: white;
    min-height: 30px;
    border-radius: 3px;
`;

export const CancelButton = styled.button`
    ${FLEX_CENTER}
    ${GrayColor}
    color: white;
    min-height: 30px;
    border-radius: 3px;
`;

export const FlexColumn = css`
    display: flex;
    flex-direction: column;
`;

export const ON_MOBILE = `screen and (max-width: ${
    pickBreakpoint(BreakPoint.md) - 1
}px)`;

export const MIN_MAX = css`
    max-width: 1600px;
    min-width: 1280px;
    @media screen and (max-width: 767px) {
        min-width: initial;
    }
`;

export const CONTENT_PADDING_WITH_HEADER = css`
    padding-top: 100px;
    z-index: 2;
    @media screen and (max-width: 767px) {
        padding-top: 40px;
    }
`;

export const MIN_MAX_WIDTH_DESKTOP = css`
    min-width: 1280px;
    max-width: 1600px;
    width: 90%;
    overflow-x: auto;
    @media screen and (max-width: 767px) {
        min-width: initial;
    }
`;

export const RespondLandscape = (...args) => {
    return css`
        @media (orientation: landscape) {
            ${args}
        }
    `;
};

export const RespondMobile = (...args) => {
    const mobileWidth = pickBreakpoint(BreakPoint.md);

    return css`
        @media screen and (max-width: ${mobileWidth - 1}px) {
            ${args}
        }
    `;
};

export const RespondPC = (...args) => {
    const mobileWidth = pickBreakpoint(BreakPoint.md);

    return css`
        @media screen and (min-width: ${mobileWidth}px) {
            ${args}
        }
    `;
};

export const ABSOLUTE_FULL_AREA = css`
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
`;

const _WHITES = ['white', COLORS.white.toLowerCase()];

export const applyHoverAndPress = memoize(
    (color = 'unset', field = 'color') => {
        const isWhite = R.contains(color.toLowerCase(), _WHITES);
        const hoverValue = isWhite ? 0.03 : 0.1;
        const activeValue = isWhite ? 0.05 : 0.2;
        return css`
            ${field}: ${color};
            &:hover {
                ${field}: ${color === 'unset'
                    ? color
                    : darken(hoverValue, color)};
            }

            &:active {
                ${field}: ${color === 'unset'
                    ? color
                    : darken(activeValue, color)};
            }
        `;
    },
    (color = 'unset', field = 'color') => color + field
);

export const gridWidth = (cnt, gutterSize) => {
    return `calc(${100 / cnt}% - ${((cnt - 1) * gutterSize) / cnt}px)`;
};

export const square = (value, unit = 'px') =>
    css`
        width: ${`${value}${unit}`};
        height: ${`${value}${unit}`};
    `;

export const FLEX_VERTICAL_CENTER = css`
    display: flex;
    align-items: center;
`;

export const BTN_HEIGHT = css`
    height: 56px;
    @media screen and (max-width: 767px) {
        height: 44px;
    }
`;

export { COLORS };

export const border = (color, width = '1px', style = 'solid') =>
    css`
        border: ${`${width} ${style} ${color}`};
    `;

export const campaignStatusStyle = ({ status }) => {
    switch (status) {
        case 'running':
            return css`
                background-color: ${COLORS['blue-60']};
                color: ${COLORS['white']};
            `;
        case 'deactivated':
            return css`
                background-color: ${COLORS['gray-40']};
                color: ${COLORS['white']};
            `;
        case 'in_progress':
        case 'onboarding':
            return css`
                border: 1px solid ${COLORS['red-60']};
                color: ${COLORS['red-60']};
                background-color: transparent;
            `;
        case 'in_review_express':
            return css`
                background-color: ${COLORS['yellow']};
                color: ${COLORS['white']};
            `;
        case 'in_review':
        case 'proposal_review':
            return css`
                color: ${COLORS['gray-80']};
                border: 1px solid ${COLORS['red-80']};
                background-color: transparent;
            `;
        case 'scheduled':
            return css`
                color: ${COLORS['blue-60']};
                border: 1px solid ${COLORS['blue-60']};
            `;
        case 'disapproved':
            return css`
                background: ${COLORS['red-60']};
                color: ${COLORS['white']};
            `;
    }
};

export const GET_COLOR = R.curryN(
    2,
    memoize(
        (prefix, num) => COLORS[[prefix, num].join('-')],
        R.unapply(R.join('-'))
    )
);

export const BLUE = GET_COLOR('blue');
export const GRAY = GET_COLOR('gray');
export const BLACK = GET_COLOR('black');
export const RED = GET_COLOR('red');
export const YELLOW = GET_COLOR('yellow');
export const LIGHT_BLUE = GET_COLOR('lightblue');
export const ORANGE = GET_COLOR('orange');

export const emptyCss = css``;

export const DIRECTIVE_CLAMP = {
    directives: [
        {
            name: 'line-clamp',
            value: 2,
        },
    ],
};

// COLORS
export const GRAY_COLOR = {
    50: css`
        color: ${GRAY(50)};
    `,
    100: css`
        color: ${GRAY(100)};
    `,
};

export const BLUE_COLOR = {
    60: css`
        color: ${BLUE(60)};
    `,
};

// FONT SIZES
export const FONT_SIZE = {
    13: css`
        font-size: 13px;
    `,
    14: css`
        font-size: 13px;
    `,
    16: css`
        font-size: 16px;
    `,
    18: css`
        font-size: 18px;
    `,
};

export const FONT_WEIGHT = {
    500: css`
        font-weight: 500;
    `,
    600: css`
        font-weight: 600;
    `,
};

export const FLEX_SHRINK_0 = css`
    flex-shrink: 0;
`;

export const WIDTH_100_PERCENT = css`
    width: 100%;
`;

export const CURSOR_POINTER = css`
    cursor: pointer;
`;

export const PADDING = {
    24: css`
        padding: 24px;
    `,
};

export const EMPTY_CSS = css``;

export const WIDGET_BACKGROUND_COLOR = '#E5E5E9';
export const WIDGET_MARGIN = 20;
export const WIDGET_BORDER_RADIUS = 4;
export const WIDGET_MARGIN_IN_REPORT_MODE = 6;
